import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import {
  selectFilteredProfessionals,
  setSortBy,
  fetchProfessionals
} from '../redux/slices/professionalsSlice';
import ProfessionalsSearchBar from '../components/ProfessionalsSearchBar';
import ProfessionalCard from '../components/ProfessionalCard';
import ProfessionalsPagination from '../components/ProfessionalsPagination';
import './Professionals.css';

const Professionals = () => {
  const dispatch = useDispatch();
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'
  
  const {
    loading,
    error,
    sortBy,
    filters,
    itemsPerPage
  } = useSelector(state => state.professionals);
  
  const paginationData = useSelector(selectFilteredProfessionals);
  const { paginatedProfessionals, filteredProfessionals } = paginationData;
  
  // Pagination is now handled by Redux selector
  React.useEffect(() => {
    // Charger les professionnels depuis l'API au montage et lors des changements de filtres/tri
    dispatch(fetchProfessionals());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // Rafraîchir depuis l'API lors des changements de filtres/tri/taille page
    dispatch(fetchProfessionals());
  }, [dispatch, filters, sortBy, itemsPerPage]);
  
  const handleSortChange = (newSortBy) => {
    dispatch(setSortBy(newSortBy));
  };
  
  // Event handlers are now in ProfessionalCard component
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  // En cas d'erreur backend, on affiche un bandeau d'alerte mais on conserve l'UI
  
  return (
    <>
      <Helmet>
        <title>Professionnels du Sport - Mission Sport</title>
        <meta name="description" content="Découvrez notre réseau de professionnels du sport qualifiés : coachs, kinésithérapeutes, arbitres et plus encore. Trouvez le professionnel idéal pour vos besoins sportifs." />
        <meta property="og:title" content="Professionnels du Sport - Mission Sport" />
        <meta property="og:description" content="Réseau de professionnels du sport qualifiés pour tous vos besoins sportifs" />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <main className="professionals-page">
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {error && (
            <Box mb={2}>
              <Alert severity="warning">{error}</Alert>
            </Box>
          )}
          {/* En-tête */}
          <Box mb={4}>
            <Typography variant="h3" component="h1" gutterBottom className="page-title">
              Professionnels du Sport
            </Typography>
            <Typography variant="h6" color="text.secondary" className="page-subtitle">
              Découvrez notre réseau de {filteredProfessionals.length} professionnels qualifiés
            </Typography>
          </Box>
          
          {/* Barre de recherche et filtres */}
           <Box mb={4}>
             <ProfessionalsSearchBar />
           </Box>
          
          {/* Options de tri et mode d'affichage */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} className="controls-bar">
            {/* Options de tri */}
            <Box display="flex" gap={1} flexWrap="wrap">
              {[
                { value: 'rating', label: 'Mieux notés' },
                { value: 'price_asc', label: 'Prix croissant' },
                { value: 'price_desc', label: 'Prix décroissant' },
                { value: 'experience', label: 'Expérience' },
                { value: 'name', label: 'Nom' }
              ].map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  variant={sortBy === option.value ? 'filled' : 'outlined'}
                  color={sortBy === option.value ? 'primary' : 'default'}
                  size="small"
                  onClick={() => handleSortChange(option.value)}
                  className="sort-chip"
                />
              ))}
            </Box>
            
            {/* Boutons de mode d'affichage */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(event, newViewMode) => {
                if (newViewMode !== null) {
                  setViewMode(newViewMode);
                }
              }}
              size="small"
              aria-label="mode d'affichage"
            >
              <ToggleButton value="grid" aria-label="affichage grille">
                 <ViewModuleIcon />
               </ToggleButton>
               <ToggleButton value="list" aria-label="affichage liste">
                 <ViewListIcon />
               </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          
          {/* Grille des professionnels */}
          {paginatedProfessionals.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Aucun professionnel trouvé
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Essayez de modifier vos critères de recherche
              </Typography>
            </Box>
          ) : (
            viewMode === 'grid' ? (
              <Grid container spacing={3} className="professionals-grid">
                {paginatedProfessionals.map((professional) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={professional.id}>
                    <ProfessionalCard professional={professional} variant="grid" />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box>
                {paginatedProfessionals.map((professional) => (
                  <ProfessionalCard 
                    key={professional.id} 
                    professional={professional} 
                    variant="list" 
                  />
                ))}
              </Box>
            )
          )}
          
          {/* Pagination */}
          <ProfessionalsPagination />
        </Container>
      </main>
    </>
  );
};

export default Professionals;